import { Button, Col, Row } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import "react-circular-progressbar/dist/styles.css";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import RotatePhonePopup from "../RotatePhonePopup";
import NoInternet from '../NoInternet';
import NoDataFound2 from '../NoData2';
import Lottie from 'lottie-react';
import { useSnackbar } from "../context/SnackbarContext";

import angryLottie from '../../lottie/angry.json';
import loveLottie from '../../lottie/love.json';
import happyLottie from '../../lottie/happy.json';
import sadLottie from '../../lottie/sad.json';

// img
import bg from "../../img/btnbox2.svg"
import enbutton from "../../img/enbutton.png"
import hibutton from "../../img/hibutton.png"
import enhibutton from "../../img/enhibutton.png"
import mrbutton from "../../img/mrbutton.png"
import tabutton from "../../img/tabutton.png"
import star from "../../img/star2.png"

import NoNet from "../../img/NoNet.png"
import NoNetDark from "../../img/NoNetDark.png"
import Nouser from "../../img/NoUserFound.png";
import NouserDark from "../../img/NoUserFoundDark.png";
import spinWin from "../../audio/spinwin.wav";
import { useLanguage } from "../context/Languagecontext";
import { useTranslatedText } from "../../hooks/useTranslatedText";
import { convertToNativeNumerals } from "../../utils/numberUtils";

// ── storeImageInLocalStorage kept for starImage only ────────────
const storeImageInLocalStorage = (key, url) => {
    const stored = localStorage.getItem(key);
    if (stored) return Promise.resolve(stored);

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            try {
                localStorage.setItem(key, dataURL);
            } catch (e) {
                console.warn(`localStorage quota exceeded for key "${key}", using in-memory fallback.`);
            }
            resolve(dataURL);
        };
        img.onerror = reject;
    });
};

const getInitialDarkMode = () => {
    if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
};


const Category4 = ({ hint, uploadedImage, originalData, username, category }) => {
    const { lanText, t } = useLanguage();
    const [decibel, setDecibel] = useState(0);
    const [isListening, setIsListening] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [activeLottie, setActiveLottie] = useState(null);
    const navigate = useNavigate();
    const [micPermissionGranted, setMicPermissionGranted] = useState(false);
    const [isRecordingComplete, setIsRecordingComplete] = useState(false);
    const [finalDecibel, setFinalDecibel] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [isCheckingPermission, setIsCheckingPermission] = useState(false);
    const [disable, setDisable] = useState(false);
    const [noInternet, setNoInternet] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);

    // ── noNet/noUser: read-only from localStorage (written by CategoryLink) ──
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [noNetImgSrc, setNoNetImgSrc] = useState(null);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noUserImgLoaded, setNoUserImgLoaded] = useState(false);
    const [noUserImgSrc, setNoUserImgSrc] = useState(null);

    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);

    // ── starImage: full cache cycle (read + write) ───────────────
    const [starImgSrc, setStarImgSrc] = useState(null);
    const [starImgLoaded, setStarImgLoaded] = useState(false);

    const [alertModal, setAlertModal] = useState({
        show: false,
        message: '',
        title: 'Alert'
    });
    const [alertModal2, setAlertModal2] = useState({
        show: false,
        message: '',
        title: 'Alert'
    });

    const { showSnackbar } = useSnackbar();

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const listeningTimerRef = useRef(null);
    const navigationTimerRef = useRef(null);
    const hasNavigatedRef = useRef(false);
    const streamRef = useRef(null);
    const permissionCheckRef = useRef(false);
    const isRecordingRef = useRef(false);
    const isStartingRecordingRef = useRef(false);
    const spinWinAudioRef = useRef(null);

    const recognitionRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const microphoneRef = useRef(null);
    const dataArrayRef = useRef(null);
    const animationFrameRef = useRef(null);
    const recentDecibelsRef = useRef([]);
    const finalDecibelRef = useRef(0);
    const recordingStartTimeRef = useRef(null);

    const getBackgroundColor = (decibel) => {
        const tubeDecibel = Math.max(decibel - 15, 0);
        if (tubeDecibel <= 22.5) return "#6ECFFB";
        if (tubeDecibel <= 45) return "#FFB95E";
        if (tubeDecibel <= 67.5) return "#FF92C0";
        if (tubeDecibel <= 100) return "#F37676";
        return "#6ECFFB";
    };

    const buttonImages = {
        en: enbutton,
        hi: hibutton,
        enhi: enhibutton,
        mr: mrbutton,
        ta: tabutton,
    };

    const preloadImages = [
        bg,
        enbutton,
        hibutton,
        enhibutton,
        mrbutton,
        tabutton,
        star
    ];

    const lottieAnimations = {
        angry: angryLottie,
        love: loveLottie,
        happy: happyLottie,
        sad: sadLottie
    };

    preloadImages.forEach((src) => {
        const img = new Image();
        img.src = src;
    });

    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;
    const isSmallScreenBottom = window.innerWidth < 380;
    const isInAppBrowser = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return /Instagram|FBAN|FBAV|Messenger|Telegram|FxiOS|Firefox|OPR|Opera|Brave/i.test(ua);
    };

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isiOSSafari = isIOS && isSafari;
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (!sessionStorage.getItem('micDeniedCount')) {
        sessionStorage.setItem('micDeniedCount', '0');
    }

    const isMobile = () => {
        return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const showAlert = (message, title = 'Alert') => {
        if (isMobile()) {
            setAlertModal({ show: true, message, title });
        } else {
            setAlertModal({ show: true, message, title });
        }
    };

    const showAlert2 = (message, title = 'Alert') => {
        if (isMobile()) {
            setAlertModal2({ show: true, message, title });
        } else {
            setAlertModal2({ show: true, message, title });
        }
    };

    const displayDecibel = isRecordingComplete
        ? Math.max(finalDecibel, 20)
        : decibel;

    const circleFillPercent = Math.min((displayDecibel / 5) * 100, 100);
    const tubeDecibel = Math.max(displayDecibel - 15, 0);

    const tubeSeg1 = Math.min(tubeDecibel, 22.5) / 22.5 * 25;
    const tubeSeg2 = Math.min(Math.max(tubeDecibel - 22.5, 0), 22.5) / 22.5 * 25;
    const tubeSeg3 = Math.min(Math.max(tubeDecibel - 45, 0), 22.5) / 22.5 * 25;
    const tubeSeg4 = Math.min(Math.max(tubeDecibel - 67.5, 0), 22.5) / 22.5 * 25;

    const getBarCount = () => {
        const screenHeight = window.innerHeight;
        if (screenHeight <= 400) return 4;
        if (screenHeight <= 600) return 7;
        if (screenHeight <= 700) return 6;
        if (screenHeight <= 800) return 8;
        return 8;
    };

    const barCount = getBarCount();

    const StripeBars = ({ count, segmentPercent, activeColor, segmentIndex }) => {
        const activeCount = Math.round((segmentPercent / 25) * count);

        return (
            <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "2px 0",
            }}>
                {[...Array(count)].map((_, i) => {
                    let marginLeft = 20;
                    if (segmentIndex === 0) {
                        if (i === 0 || i === count - 1) marginLeft = 0;
                    } else {
                        if (i === 0) marginLeft = 0;
                    }
                    const isActive = i >= (count - activeCount);
                    return (
                        <div
                            key={i}
                            style={{
                                height: "4px",
                                width: "100%",
                                background: isActive ? activeColor : "#D9D9D9",
                                marginLeft,
                                transition: "0.3s"
                            }}
                        ></div>
                    );
                })}
            </div>
        );
    };

    const data = originalData.question;

    const questionText = Array.isArray(data)
        ? data.find(item => item.category === category)?.question || t.emotion.defaultQuestion
        : t.emotion.defaultQuestion;
    const translatedQuestion = useTranslatedText(questionText, lanText);

    const wordText = Array.isArray(data)
        ? data.find(item => item.category === category)?.answer.speakWord ?? t.emotion.defaultWord
        : t.emotion.defaultWord;
    const translatedWord = useTranslatedText(wordText, lanText);

    const measureLoudness = () => {
        if (!analyserRef.current || !isRecordingRef.current) {
            animationFrameRef.current = null;
            return;
        }

        try {
            const bufferLength = analyserRef.current.fftSize;
            if (!dataArrayRef.current || dataArrayRef.current.length !== bufferLength) {
                dataArrayRef.current = new Uint8Array(bufferLength);
            }

            analyserRef.current.getByteTimeDomainData(dataArrayRef.current);

            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const value = (dataArrayRef.current[i] - 128) / 128;
                sum += value * value;
            }

            const rms = Math.sqrt(sum / bufferLength);
            const scaledRms = rms * 2;
            const dB = scaledRms > 0 ? 20 * Math.log10(scaledRms) : -100;
            const peakScore = calculateScore(dB);
            const scoreValue = parseFloat(peakScore.toFixed(0));

            setDecibel(scoreValue);

            recentDecibelsRef.current.push(parseFloat(dB.toFixed(2)));
            if (recentDecibelsRef.current.length > 20) {
                recentDecibelsRef.current.shift();
            }

            if (isRecordingRef.current) {
                animationFrameRef.current = requestAnimationFrame(measureLoudness);
            } else {
                animationFrameRef.current = null;
            }
        } catch (error) {
            console.warn('Error in measureLoudness:', error);
            animationFrameRef.current = null;
        }
    };

    const calculateScore = (dB) => {
        const db = parseFloat(dB);
        if (db <= -50) return 0;
        if (db > -50 && db <= -40) return ((db + 50) / 10) * 10;
        if (db > -40 && db <= -30) return ((db + 40) / 10) * 15 + 10;
        if (db > -30 && db <= -20) return ((db + 30) / 10) * 20 + 25;
        if (db > -20 && db <= -12) return ((db + 20) / 8) * 15 + 45;
        if (db > -12 && db <= -6) return ((db + 12) / 6) * 25 + 60;
        if (db > -6) return Math.min(100, 85 + (db + 6) * 5);
        return 0;
    };

    const getAudioDuration = (blob) => {
        return new Promise((resolve) => {
            const audio = new Audio();
            audio.preload = 'metadata';
            audio.onloadedmetadata = () => {
                window.URL.revokeObjectURL(audio.src);
                resolve(audio.duration);
            };
            audio.onerror = () => { resolve(0); };
            audio.src = URL.createObjectURL(blob);
        });
    };

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 200));
        if (!navigator.onLine) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            await fetch('https://www.google.com/favicon.ico', {
                mode: 'no-cors',
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            return false;
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log("Tab hidden/minimized - stopping microphone");

                if (isRecordingRef.current) {
                    isRecordingRef.current = false;
                    setIsRecording(false);
                    setDecibel(0);
                    setActiveLottie(null);
                    isStartingRecordingRef.current = false;
                }

                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }

                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                    try { mediaRecorderRef.current.stop(); } catch (e) { }
                    mediaRecorderRef.current = null;
                }

                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => {
                        track.stop();
                        console.log("Track stopped on visibility hidden:", track.kind);
                    });
                    streamRef.current = null;
                }

                if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                    audioContextRef.current.close().catch(e => console.warn("Audio context close error:", e));
                    audioContextRef.current = null;
                }

                recentDecibelsRef.current = [];
                recordingStartTimeRef.current = null;
                audioChunksRef.current = [];
                permissionCheckRef.current = false;
            } else {
                console.log("Tab visible again - mic will re-initialize on next press");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => { document.removeEventListener('visibilitychange', handleVisibilityChange); };
    }, []);

    useEffect(() => {
        spinWinAudioRef.current = new Audio(spinWin);
        spinWinAudioRef.current.preload = "auto";
        spinWinAudioRef.current.loop = false;
        spinWinAudioRef.current.load();
    }, []);

    useEffect(() => {
        const imagesToPreload = [
            bg,
            buttonImages[lanText] || enbutton,
            star
        ];

        let loadedCount = 0;
        let hasError = false;
        const totalImages = imagesToPreload.length;

        const timeoutId = setTimeout(() => {
            if (!imagesLoaded && !hasError) {
                setImageLoadError(true);
            }
        }, 5000);

        const checkAllLoaded = () => {
            loadedCount++;
            if (loadedCount === totalImages && starImgLoaded) {
                clearTimeout(timeoutId);
                setTimeout(() => { setImagesLoaded(true); }, 500);
            }
        };

        const handleError = () => {
            hasError = true;
            clearTimeout(timeoutId);
            setImageLoadError(true);
        };

        imagesToPreload.forEach((src) => {
            const img = new Image();
            img.onload = checkAllLoaded;
            img.onerror = handleError;
            img.src = src;
        });

        return () => clearTimeout(timeoutId);
    }, [lanText, starImgLoaded]);

    useEffect(() => {
        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(darkModeMediaQuery.matches);
        const handleChange = (e) => setIsDarkMode(e.matches);
        darkModeMediaQuery.addEventListener('change', handleChange);
        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    // ── Image loading effect ─────────────────────────────────────
    // noNet/noUser: read-only from localStorage (written by CategoryLink)
    // starImage:    full read+write cache cycle
    useEffect(() => {
        let isMounted = true;

        // ── noNet / noUser: simple reads, fallback to imported src ──
        const noNetCached = localStorage.getItem('noNetImage');
        const noNetDarkCached = localStorage.getItem('noNetImageDark');
        const noUserCached = localStorage.getItem('noUserImage');
        const noUserDarkCached = localStorage.getItem('noUserImageDark');

        if (isMounted) {
            setNoNetImgSrc(noNetCached || NoNet);
            setNoNetImgLoaded(true);
            setNoNetDarkImgSrc(noNetDarkCached || NoNetDark);
            setNoUserImgSrc(noUserCached || Nouser);
            setNoUserImgLoaded(true);
            setNoUserDarkImgSrc(noUserDarkCached || NouserDark);
        }

        // ── starImage: full cache cycle (read + write) ───────────
        storeImageInLocalStorage('starImage', star)
            .then((dataURL) => {
                if (isMounted) {
                    setStarImgSrc(dataURL);
                    setStarImgLoaded(true);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setStarImgSrc(star);
                    setStarImgLoaded(true);
                }
            });

        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        let initialPermissionState = 'prompt';

        const checkInternet = async () => {
            const isOnline = await checkInternetConnection();
            if (!isOnline) { setNoInternet(true); return false; }
            return true;
        };

        const checkMicPermission = async () => {
            const hasInternet = await checkInternet();
            if (!hasInternet) return;

            if (permissionCheckRef.current || isCheckingPermission) return;

            permissionCheckRef.current = true;
            setIsCheckingPermission(true);

            try {
                if (navigator.permissions) {
                    try {
                        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                        initialPermissionState = permissionStatus.state;

                        if (initialPermissionState === 'denied') {
                            if (isAndroid) {
                                showAlert(t.blockedIOS || "Microphone permission is blocked. To enable:\n1. Tap the three dots (⋮) in browser\n2. Go to Site settings\n3. Tap Microphone\n4. Select 'Allow'");
                            } else if (isiOSSafari) {
                                showAlert2(t.blockedIOS || "Microphone permission is blocked. Please enable it in your browser settings.");
                            } else {
                                showAlert(t.blockedIOS || "Microphone permission is blocked. Please enable it in your browser settings.");
                            }
                            setMicPermissionGranted(false);
                            setIsCheckingPermission(false);
                            permissionCheckRef.current = false;
                            setDisable(true);
                            return;
                        }
                    } catch (permError) {
                        console.log('Permission query not supported');
                    }
                }

                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = stream;

                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 2048;
                const micSource = audioContextRef.current.createMediaStreamSource(stream);
                micSource.connect(analyserRef.current);

                setMicPermissionGranted(true);
                setIsCheckingPermission(false);

            } catch (error) {
                console.error('Microphone permission error:', error);
                setMicPermissionGranted(false);
                setIsCheckingPermission(false);
                permissionCheckRef.current = false;

                if (error.name === 'NotAllowedError') {
                    if (navigator.permissions) {
                        try {
                            const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                            if (permissionStatus.state === 'denied' && initialPermissionState === 'denied') {
                                if (isAndroid) {
                                    showAlert(t.blockedIOS || "Microphone permission is blocked. To enable:\n1. Tap the three dots (⋮) in browser\n2. Go to Site settings\n3. Tap Microphone\n4. Select 'Allow'");
                                } else if (isiOSSafari) {
                                    showAlert2(t.blockedIOS || "Microphone permission is blocked. Please enable it in your browser settings.");
                                } else {
                                    showAlert(t.blockedIOS || "Microphone permission is blocked. Please enable it in your browser settings.");
                                }
                                setDisable(true);
                            } else {
                                if (isiOSSafari) {
                                    let micDeniedCount = parseInt(sessionStorage.getItem('micDeniedCount'), 10) || 0;
                                    micDeniedCount += 1;
                                    sessionStorage.setItem('micDeniedCount', micDeniedCount.toString());
                                    if (micDeniedCount >= 3) {
                                        showAlert2(t.blockedIOS || "Microphone permission is blocked. Please enable it in your browser settings.");
                                    } else {
                                        showAlert2(t.whoTalkin.blockedIOS || "Microphone permission is blocked.");
                                    }
                                } else {
                                    showAlert(t.whoTalkin.micNotFound || "No microphone found on your device.");
                                }
                                setDisable(true);
                            }
                        } catch (permError) {
                            if (isiOSSafari) {
                                let micDeniedCount = parseInt(sessionStorage.getItem('micDeniedCount'), 10) || 0;
                                micDeniedCount += 1;
                                sessionStorage.setItem('micDeniedCount', micDeniedCount.toString());
                                if (micDeniedCount >= 3) {
                                    showAlert2(t.whoTalkin.blockedIOS || "Microphone permission is blocked.");
                                } else {
                                    showAlert2(t.whoTalkin.blockedIOS || "Microphone permission is blocked.");
                                }
                            } else {
                                showAlert(t.whoTalkin.micNotFound || "No microphone found on your device.");
                            }
                            setDisable(true);
                        }
                    }
                } else if (error.name === 'NotFoundError') {
                    showAlert(t.whoTalkin.micNotFound || "No microphone found on your device.");
                    setDisable(true);
                } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                    showAlert(t.whoTalkin.micInUse || "Microphone is being used by another application.");
                    setDisable(true);
                }
            }
        };

        checkMicPermission();

        return () => {
            console.log("Component unmounting, cleaning up...");

            if (navigationTimerRef.current) { clearTimeout(navigationTimerRef.current); navigationTimerRef.current = null; }
            if (listeningTimerRef.current) { clearTimeout(listeningTimerRef.current); listeningTimerRef.current = null; }
            if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                try { mediaRecorderRef.current.stop(); } catch (e) { }
                mediaRecorderRef.current = null;
            }

            if (microphoneRef.current) {
                try { microphoneRef.current.disconnect(); } catch (e) { }
                microphoneRef.current = null;
            }

            if (analyserRef.current) {
                try { analyserRef.current.disconnect(); } catch (e) { }
                analyserRef.current = null;
            }

            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(e => console.warn("Error closing audio context:", e));
                audioContextRef.current = null;
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => {
                    track.stop();
                    console.log("Track stopped on unmount:", track.kind, track.readyState);
                });
                streamRef.current = null;
            }

            isStartingRecordingRef.current = false;
            isRecordingRef.current = false;
            hasNavigatedRef.current = false;
            permissionCheckRef.current = false;
        };
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            console.log("Back button pressed - cleaning up audio resources");
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
            if (audioContextRef.current) audioContextRef.current.close().catch(() => { });
        };

        window.addEventListener('popstate', handlePopState);
        return () => { window.removeEventListener('popstate', handlePopState); };
    }, []);

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const navigateToNextScreen = async (blob, finalDecibelValue) => {
        if (hasNavigatedRef.current || isNavigating) return;

        hasNavigatedRef.current = true;
        setIsNavigating(true);

        try {
            console.log("Starting cleanup before navigation...");

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
                await new Promise(resolve => setTimeout(resolve, 100));
                mediaRecorderRef.current = null;
            }

            if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
            if (microphoneRef.current) { microphoneRef.current.disconnect(); microphoneRef.current = null; }
            if (analyserRef.current) { analyserRef.current.disconnect(); analyserRef.current = null; }

            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
                await new Promise(resolve => setTimeout(resolve, 300));
            }

        } catch (error) {
            console.error("Error during cleanup:", error);
            await new Promise(resolve => setTimeout(resolve, 300));
        }

        setDecibel(0);
        setIsListening(false);
        setIsRecording(false);
        setActiveLottie(null);
        setIsRecordingComplete(false);
        setFinalDecibel(0);
        setMicPermissionGranted(false);
        setIsCheckingPermission(false);
        setDisable(false);
        setNoInternet(false);
        setImagesLoaded(false);
        setImageLoadError(false);

        recentDecibelsRef.current = [];
        finalDecibelRef.current = 0;
        recordingStartTimeRef.current = null;
        audioChunksRef.current = [];
        isRecordingRef.current = false;
        isStartingRecordingRef.current = false;
        permissionCheckRef.current = false;

        const micDeniedCount = sessionStorage.getItem('micDeniedCount');

        window.scrollTo(0, 0);

        const audioBase64 = await blobToBase64(blob);

        sessionStorage.clear();

        if (micDeniedCount) sessionStorage.setItem('micDeniedCount', micDeniedCount);
        sessionStorage.setItem("audioBlob", audioBase64);

        const formData = {
            originalData,
            hint,
            uploadedImage,
            translatedQuestion,
            decibel: finalDecibelValue,
            timestamp: Date.now()
        };

        sessionStorage.setItem('emotionFlowData', JSON.stringify(formData));

        navigate(`/${username || ''}/${category || ''}/emotioncard`, {
            state: formData,
            replace: false
        });
    };

    const handlePressStart = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log("=== PRESS START ===");

        if (!navigator.onLine) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");
            return;
        }

        if (!micPermissionGranted) {
            showSnackbar(t?.emotion?.permissionError || "Microphone permission not granted. Please allow access.", "error");
            return;
        }

        if (isRecording || isRecordingComplete || isNavigating || isCheckingPermission || disable) {
            console.log("Action blocked - already in progress or disabled");
            return;
        }

        if (isStartingRecordingRef.current) {
            console.log("Already starting recording, ignoring duplicate event");
            return;
        }

        isStartingRecordingRef.current = true;

        try {
            await startListening();
        } catch (error) {
            console.error("Error in handlePressStart:", error);
            isStartingRecordingRef.current = false;
        }
    };

    const handlePressEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log("=== PRESS END ===");

        if (isRecording && !isRecordingComplete && !isNavigating) {
            console.log("Stopping recording...");
            stopListening();
        } else {
            console.log("Not stopping - not in recording state");
            isStartingRecordingRef.current = false;
        }
    };

    const startListening = async () => {
        console.log("=== START LISTENING ===");

        if (!micPermissionGranted) {
            isStartingRecordingRef.current = false;
            return;
        }

        try {
            if (!streamRef.current || !streamRef.current.active) {
                console.log("Stream not available or inactive, recreating...");

                const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = newStream;

                if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                    await audioContextRef.current.close();
                }

                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 2048;
                const micSource = audioContextRef.current.createMediaStreamSource(newStream);
                micSource.connect(analyserRef.current);
            } else {
                console.log("Using existing active stream");
                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                    console.log("Resuming suspended audio context");
                    await audioContextRef.current.resume();
                }
            }

            const mediaRecorder = new MediaRecorder(streamRef.current);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';

            recordingStartTimeRef.current = Date.now();
            console.log("Recording start time set:", recordingStartTimeRef.current);

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                    console.log("Audio chunk added, size:", event.data.size);
                }
            };

            mediaRecorder.onstop = async () => {
                console.log("=== MEDIA RECORDER STOPPED ===");
                const recordingDuration = (Date.now() - recordingStartTimeRef.current) / 1000;
                console.log("Recording duration:", recordingDuration, "seconds");

                if (recordingDuration < 1) {
                    console.log("Recording too short!");
                    isStartingRecordingRef.current = false;
                    setIsRecording(false);
                    isRecordingRef.current = false;
                    showSnackbar(t?.emotion?.recordError || "Please hold and press longer.", "error");
                    return;
                }

                const blob = new Blob(audioChunksRef.current, { type: mimeType });
                console.log("Created blob, size:", blob.size);

                const audioDuration = await getAudioDuration(blob);
                console.log("Audio duration from blob:", audioDuration, "seconds");

                setIsRecordingComplete(true);

                const currentFinalDecibel = finalDecibelRef.current || parseFloat(decibel) || 20;
                setAudioBlob(blob);
                setFinalDecibel(currentFinalDecibel);
                console.log("Final decibel:", currentFinalDecibel);

                if (!isIOS) {
                    navigationTimerRef.current = setTimeout(() => {
                        navigateToNextScreen(blob, currentFinalDecibel);
                    }, 3000);
                }
            };

            mediaRecorder.onerror = (error) => {
                console.error("MediaRecorder error:", error);
                isStartingRecordingRef.current = false;
                setIsRecording(false);
                isRecordingRef.current = false;
            };

            mediaRecorder.start();
            console.log("MediaRecorder started");

            setIsRecording(true);
            isRecordingRef.current = true;
            setDecibel(0);
            setActiveLottie(null);

            isStartingRecordingRef.current = false;
            console.log("Reset isStartingRecordingRef to false after successful start");

            measureLoudness();

        } catch (error) {
            console.error('Error starting recording:', error);
            isStartingRecordingRef.current = false;
            showSnackbar(t?.emotion?.recordError || "Error starting recording. Please try again.", "error");
        }
    };

    const stopListening = () => {
        if (hasNavigatedRef.current || isNavigating) {
            console.log("Already navigating, skipping stopListening");
            return;
        }

        console.log("=== STOP LISTENING ===");
        const wasRecording = isRecordingRef.current;
        isRecordingRef.current = false;
        setIsRecording(false);

        const recordingDuration = recordingStartTimeRef.current
            ? (Date.now() - recordingStartTimeRef.current) / 1000
            : 0;

        console.log("Recording duration:", recordingDuration, "seconds");

        const cleanupRecording = () => {
            console.log("Cleaning up recording resources...");

            if (listeningTimerRef.current) { clearTimeout(listeningTimerRef.current); listeningTimerRef.current = null; }
            if (navigationTimerRef.current) { clearTimeout(navigationTimerRef.current); navigationTimerRef.current = null; }

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                try {
                    console.log("Stopping MediaRecorder, state:", mediaRecorderRef.current.state);
                    mediaRecorderRef.current.stop();
                } catch (err) {
                    console.warn('Error stopping media recorder:', err);
                    mediaRecorderRef.current = null;
                    recordingStartTimeRef.current = null;
                }
            } else {
                console.log("MediaRecorder already inactive or null");
                recordingStartTimeRef.current = null;
            }

            if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }

            if (recognitionRef.current) {
                try { recognitionRef.current.stop(); } catch (err) { console.warn('Error stopping recognition:', err); }
                recognitionRef.current = null;
            }

            if (microphoneRef.current) {
                try { microphoneRef.current.disconnect(); } catch (err) { console.warn('Error disconnecting microphone:', err); }
                microphoneRef.current = null;
            }

            console.log("Keeping stream alive for next recording");
            setIsListening(false);
            console.log("Cleanup complete");
        };

        if (!wasRecording || recordingDuration < 1) {
            console.log("Recording too short or never started:", recordingDuration);

            cleanupRecording();
            isStartingRecordingRef.current = false;
            setDecibel(0);
            setActiveLottie(null);

            showSnackbar(t?.emotion?.recordError || "Please hold and press longer.", "error");

            setTimeout(() => {
                recordingStartTimeRef.current = null;
                if (mediaRecorderRef.current) mediaRecorderRef.current = null;
            }, 100);

            return;
        }

        console.log("Normal stop, cleaning up...");
        cleanupRecording();

        const readingsPerSecond = 60;
        let currentDecibel;

        if (recentDecibelsRef.current.length > 0) {
            let relevantReadings;

            if (recordingDuration <= 2) {
                const samplesToUse = Math.min(readingsPerSecond * 2, recentDecibelsRef.current.length);
                const endIndex = recentDecibelsRef.current.length - 5;
                const startIndex = Math.max(0, endIndex - samplesToUse);
                relevantReadings = recentDecibelsRef.current.slice(startIndex, endIndex);
                console.log(`Recording ≤2s (${recordingDuration.toFixed(2)}s): Using last ${relevantReadings.length} samples`);
            } else {
                const onSecondSamples = readingsPerSecond;
                const endIndex = recentDecibelsRef.current.length - 5;
                const startIndex = Math.max(0, endIndex - onSecondSamples);
                relevantReadings = recentDecibelsRef.current.slice(startIndex, endIndex);
                console.log(`Recording >2s (${recordingDuration.toFixed(2)}s): Using 1 second before end (${relevantReadings.length} samples)`);
            }

            if (relevantReadings.length > 0) {
                const avgDb = relevantReadings.reduce((sum, db) => sum + db, 0) / relevantReadings.length;
                currentDecibel = calculateScore(avgDb);
                console.log("Averaged decibel:", currentDecibel, "from", relevantReadings.length, "samples");
            } else {
                currentDecibel = parseFloat(decibel) || 0;
                console.log("No samples available, using current decibel:", currentDecibel);
            }
        } else {
            currentDecibel = parseFloat(decibel) || 0;
            console.log("No decibel readings, using current decibel:", currentDecibel);
        }

        if (currentDecibel < 20) {
            currentDecibel = 20;
            setDecibel(20);
        }

        finalDecibelRef.current = currentDecibel;
        console.log("Final decibel set to:", currentDecibel);

        let newActiveLottie = null;
        const tubeDecibelValue = Math.max(currentDecibel - 15, 0);

        const seg1 = Math.min(tubeDecibelValue, 22.5) / 22.5 * 25;
        const seg2 = Math.min(Math.max(tubeDecibelValue - 22.5, 0), 22.5) / 22.5 * 25;
        const seg3 = Math.min(Math.max(tubeDecibelValue - 45, 0), 22.5) / 22.5 * 25;
        const seg4 = Math.min(Math.max(tubeDecibelValue - 67.5, 0), 22.5) / 22.5 * 25;

        if (seg4 > 0) {
            newActiveLottie = 'angry';
        } else if (seg3 > 0) {
            newActiveLottie = 'love';
        } else if (seg2 > 0) {
            newActiveLottie = 'happy';
        } else if (seg1 > 0) {
            newActiveLottie = 'sad';
        }

        setTimeout(() => {
            try {
                if (navigator.onLine && spinWinAudioRef.current) {
                    spinWinAudioRef.current.currentTime = 0;
                    spinWinAudioRef.current.play();
                }
            } catch (e) {
                console.warn("Win audio error:", e);
            }
        }, 1100);

        console.log("Active lottie:", newActiveLottie);
        setActiveLottie(newActiveLottie);
    };

    const getTopPosition = () => {
        switch (activeLottie) {
            case "sad": return "25%";
            case "happy": return "15%";
            case "love": return "-2%";
            case "angry": return "-20%";
            default: return "94px";
        }
    };

    const getTopPosition2 = () => {
        switch (activeLottie) {
            case "sad": return "25%";
            case "happy": return "15%";
            case "love": return "-2%";
            case "angry": return "-20%";
            default: return "42px";
        }
    };

    useEffect(() => {
        preloadImages.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    useEffect(() => {
        const isDisabled = isRecording || isCheckingPermission || isNavigating || !!activeLottie;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isRecording, isCheckingPermission, isNavigating, activeLottie]);

    const currentNoNetImg = isDarkMode ? noNetDarkImgSrc : noNetImgSrc;
    const currentNoUserImg = isDarkMode ? noUserDarkImgSrc : noUserImgSrc;

    if (noInternet) {
        return (
            <NoInternet
                style={{ height: '100vh' }}
                t={t?.noData || {}}
                Nouser={currentNoNetImg}
                imgLoaded={noNetImgLoaded}
            />
        );
    }

    if (imageLoadError) {
        return (
            <NoDataFound2
                style={{ height: '100vh' }}
                t={t?.noData2 || {}}
                Nouser={currentNoUserImg}
                username={username}
                category={category}
                imgLoaded={noUserImgLoaded}
            />
        );
    }

    if (!imagesLoaded) {
        return (
            <>
                <div className='GTRegular' style={{ background: isDarkMode ? "#1C1C1C" : "#fff" , height: '100dvh' }}>
                    <div className='h-100'>
                        <Row className={`h-100`}>
                            <Col className="p-0 d-flex flex-column align-items-between justify-content-between" style={{ height: '100%' }}>

                                <div className="d-flex justify-content-center align-items-center" style={{ height: '11%', padding: '20px 0' }}>
                                    <div className="shimmer" style={{ width: "85%", height: "25px", maxWidth: "90vw", borderRadius: "12px" }}></div>
                                </div>

                                <div className="d-flex justify-content-center align-items-center mt-1" style={{ minHeight: isSmallScreen ? "270px" : "49vh" }}>
                                    <div className="d-flex flex-column justify-content-center align-items-center h-100 rounded-19 shimmer-element position-relative"
                                        style={{ width: "450px", maxWidth: isSmallScreen ? "81vw" : "87vw", padding: "20px" }}>
                                        <div className="shimmer-light position-absolute" style={{ width: "75%", height: "50px", top: "-25px", borderRadius: "8px" }}></div>
                                    </div>
                                </div>

                                <div className="d-flex flex-column justify-content-center align-items-center px-3 mb-3" style={{ height: '300px', gap: "15px" }}>
                                    <div className="shimmer-light" style={{ width: "60%", height: "40px", borderRadius: "12px", maxWidth: "300px" }}></div>
                                    <div className="shimmer-element" style={{ width: "clamp(90px, 10vw, 110px)", height: "clamp(90px, 10vw, 110px)", borderRadius: "50%" }}></div>
                                </div>

                            </Col>
                        </Row>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className='yellow-bg GTRegular' style={{ height: '100dvh' }}>
            <div className='h-100 pt-42'>
                <Row className={`h-100`}>
                    <Col className="p-0 d-flex flex-column align-items-between justify-content-between" style={{ height: '100%' }}>

                        <div className="d-flex justify-content-center align-items-center" style={{ height: '12%', padding: '20px 0' }}>
                            <div className="text-center" style={{ maxWidth: "85vw" }}>
                                <p className="lh-sm mx-auto px-3 m-0 mb-2 mt-3"
                                    style={{ fontSize: "17px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {t.emotion.text} <br /> {t.emotion.text2}
                                </p>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center align-items-center mt-3" style={{ minHeight: isSmallScreen ? "300px" : "50vh" }}>
                            <div className="d-flex justify-content-center align-items-center h-100 rounded-19 border border-1 border-black position-relative"
                                style={{ width: "450px", maxWidth: isSmallScreen ? "81vw" : "87vw", background: "#FBF5E3" }}>

                                <div className="w-100 h-100 position-relative">

                                    <div className="bg-white shadow position-absolute d-flex align-items-center justify-content-center"
                                        style={{
                                            width: isSmallScreen ? "0" : "100px",
                                            height: isSmallScreen ? "0" : "100px",
                                            borderRadius: isSmallScreen ? "0px 0px 50px 50px" : "100%",
                                            bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 3
                                        }}></div>

                                    <div className="position-absolute"
                                        style={{
                                            width: isSmallScreen ? "" : "90px",
                                            height: isSmallScreen ? "" : "90px",
                                            backgroundColor: "#D9D9D9",
                                            borderRadius: isSmallScreen ? "0px" : "100%",
                                            borderLeft: isSmallScreen ? "5px solid white" : "none",
                                            borderRight: isSmallScreen ? "5px solid white" : "none",
                                            borderBottom: isSmallScreen ? "5px solid white" : "none",
                                            bottom: "25px", left: "50%", transform: "translateX(-50%)",
                                            zIndex: 5, overflow: "hidden", display: isSmallScreen ? "none" : "block"
                                        }}>
                                        <div style={{
                                            position: "absolute", bottom: "0", width: "100%",
                                            height: `${circleFillPercent}%`, background: "#6ECFFB",
                                            transition: "height 0.3s ease, background 0.3s ease",
                                        }}></div>
                                    </div>

                                    <div className="bg-white shadow rounded-pill position-absolute"
                                        style={{
                                            width: "65px", height: isSmallScreen ? "57%" : "65%",
                                            bottom: isSmallScreen ? "80px" : "90px",
                                            left: "50%", transform: "translateX(-50%)", zIndex: 2
                                        }}></div>

                                    <div className="position-absolute border border-5 border-white"
                                        style={{
                                            width: "65px",
                                            borderRadius: isSmallScreen ? "50px" : "50px 50px 0 0",
                                            height: isSmallScreen ? "76%" : "65%",
                                            backgroundColor: "#D9D9D9",
                                            bottom: isSmallScreen ? "30px" : "90px",
                                            left: "50%", transform: "translateX(-50%)", zIndex: 4, overflow: "hidden",
                                        }}>
                                        <div style={{ position: "absolute", bottom: "0%", width: "100%", height: `${tubeSeg1}%`, background: "#6ECFFB", transition: "0.3s" }}></div>
                                        <div style={{ position: "absolute", bottom: `${tubeSeg1}%`, width: "100%", height: `${tubeSeg2}%`, background: "#FFB95E", transition: "0.3s" }}></div>
                                        <div style={{ position: "absolute", bottom: `${tubeSeg1 + tubeSeg2}%`, width: "100%", height: `${tubeSeg3}%`, background: "#FF92C0", transition: "0.3s" }}></div>
                                        <div style={{ position: "absolute", bottom: `${tubeSeg1 + tubeSeg2 + tubeSeg3}%`, width: "100%", height: `${tubeSeg4}%`, background: "#F37676", transition: "0.3s" }}></div>
                                    </div>

                                    <div className="position-absolute"
                                        style={{
                                            width: "45px", height: isSmallScreen ? "62%" : "60%",
                                            bottom: isSmallScreen ? "50px" : "100px",
                                            left: "50%", transform: "translateX(-50%)",
                                            marginLeft: "-60px", zIndex: 4, overflow: "hidden",
                                        }}>
                                        <div style={{ position: "absolute", bottom: "0%", width: "100%", height: `25%`, transition: "0.3s" }}>
                                            <StripeBars count={barCount} segmentPercent={tubeSeg1} activeColor="#6ECFFB" segmentIndex={0} />
                                        </div>
                                        <div style={{ position: "absolute", bottom: `25%`, width: "100%", height: `25%`, transition: "0.3s" }}>
                                            <StripeBars count={barCount} segmentPercent={tubeSeg2} activeColor="#FFB95E" segmentIndex={1} />
                                        </div>
                                        <div style={{ position: "absolute", bottom: `50%`, width: "100%", height: `25%`, transition: "0.3s" }}>
                                            <StripeBars count={barCount} segmentPercent={tubeSeg3} activeColor="#FF92C0" segmentIndex={2} />
                                        </div>
                                        <div style={{ position: "absolute", bottom: `75%`, width: "100%", height: `25%`, transition: "0.3s" }}>
                                            <StripeBars count={barCount} segmentPercent={tubeSeg4} activeColor="#F37676" segmentIndex={3} />
                                        </div>
                                    </div>

                                    <div className="position-absolute text-end GTBold"
                                        style={{
                                            width: "45px", height: isSmallScreen ? "62%" : "60%",
                                            bottom: isSmallScreen ? "42px" : "94px",
                                            left: "50%", transform: "translateX(-50%)",
                                            marginLeft: "-110px", fontSize: "clamp(14px, 2.2vw, 16px)", zIndex: 4,
                                        }}>
                                        <div style={{ position: "absolute", bottom: "0%", width: "100%" }}>{convertToNativeNumerals(0, lanText)}</div>
                                        <div style={{ position: "absolute", bottom: `22%`, width: "100%" }}>{convertToNativeNumerals(25, lanText)}</div>
                                        <div style={{ position: "absolute", bottom: `46%`, width: "100%" }}>{convertToNativeNumerals(50, lanText)}</div>
                                        <div style={{ position: "absolute", bottom: `71%`, width: "100%" }}>{convertToNativeNumerals(75, lanText)}</div>
                                        <div style={{ position: "absolute", bottom: `95%`, width: "100%" }}>{convertToNativeNumerals(100, lanText)}</div>
                                    </div>

                                    <div className="GTMedium position-absolute text-black d-flex flex-column align-items-center justify-content-around ps-4"
                                        style={{
                                            width: isSmallScreen ? "110px" : "120px", height: isSmallScreen ? "68%" : "60%",
                                            bottom: isSmallScreen ? "42px" : "94px", left: "45%", fontSize: "14px", zIndex: 1
                                        }}>
                                        {[
                                            { color: "#F37676", label: t.emotion.angry },
                                            { color: "#FF92C0", label: t.emotion.love },
                                            { color: "#FFB95E", label: t.emotion.happy },
                                            { color: "#6ECFFB", label: t.emotion.sad },
                                        ].map(({ color, label }) => (
                                            <div key={label} className="GTMedium rounded-pill text-black d-flex align-items-center justify-content-center ps-4"
                                                style={{ width: isSmallScreen ? "110px" : "120px", height: "calc(4vh - 0px)", backgroundColor: color, fontSize: "14px", zIndex: 1 }}>
                                                <p className="m-0">{label}</p>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="GTMedium position-absolute text-black d-flex flex-column align-items-center justify-content-around"
                                        style={{
                                            width: isSmallScreen ? "110px" : "120px", height: isSmallScreen ? "68%" : "60%",
                                            bottom: isSmallScreen ? "42px" : "94px", left: "50%", transform: "translateX(-50%)",
                                            fontSize: "14px", zIndex: 6
                                        }}>
                                        {["angry", "love", "happy", "sad"].map((emotion) => (
                                            <div key={emotion} className="GTBold rounded-pill text-black d-flex align-items-center justify-content-center"
                                                style={{ height: "calc(4vh - 0px)", fontSize: "14px", zIndex: 6, opacity: activeLottie === emotion ? 0 : 1, transition: "opacity 0.1s ease 0s" }}>
                                                <Lottie animationData={lottieAnimations[emotion]} loop={true} autoplay={false}
                                                    style={{ width: isSmallScreen ? 30 : 35, height: isSmallScreen ? 30 : 35 }} />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="GTMedium position-absolute text-black d-flex flex-column align-items-center justify-content-around"
                                        style={{
                                            width: isSmallScreen ? "110px" : "120px", height: isSmallScreen ? "68%" : "60%",
                                            left: "50%", bottom: isSmallScreen ? getTopPosition2() : getTopPosition(),
                                            transform: "translateX(-50%)", fontSize: "14px", zIndex: 8, transition: "bottom 1s ease 1s"
                                        }}>
                                        {["angry", "love", "happy", "sad"].map((emotion) => (
                                            <div key={emotion} className="GTBold rounded-pill d-flex align-items-center justify-content-center"
                                                style={{
                                                    height: "calc(4vh - 0px)", fontSize: "14px", zIndex: 8,
                                                    transition: "transform 0.1s ease 0s, width 0.5s ease 1s",
                                                    transform: activeLottie === emotion ? "scale(1)" : "scale(0)",
                                                    width: activeLottie === emotion ? 180 : 35,
                                                }}>
                                                <Lottie animationData={lottieAnimations[emotion]} loop={true} autoplay={true}
                                                    style={{ width: activeLottie === emotion ? 180 : 35, height: activeLottie === emotion ? 180 : 35 }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="position-absolute rounded-2 text-center border border-1 border-black w-75 d-flex align-items-center justify-content-center p-2 GTBold"
                                    style={{
                                        height: "50px", background: "#FBF5E3", fontWeight: "900", top: "-25px",
                                        fontSize: translatedQuestion.length > 60 ? "clamp(11px, 2vw, 14px)"
                                            : translatedQuestion.length > 40 ? "clamp(14px, 2.2vw, 16px)"
                                                : "clamp(15px, 2.5vw, 18px)",
                                    }}>
                                    <p className="m-0" style={{ lineHeight: "16px" }}>{t?.common?.que || 'Que:'} {' '}{translatedQuestion}</p>
                                </div>

                            </div>
                        </div>

                        <div className="d-flex justify-content-center align-items-center px-3 mb-3" style={{ height: '300px' }}>
                            <div className="position-relative my-3 d-flex flex-column"
                                style={{
                                    height: isSmallScreenBottom ? "150px" : "175px", width: "100%", maxWidth: "500px",
                                    backgroundImage: `url(${bg})`, backgroundSize: "contain",
                                    backgroundRepeat: "no-repeat", backgroundPosition: "center",
                                }}>
                                <div className="GTBold d-flex align-items-center justify-content-center px-4 pt-1 text-center"
                                    style={{ flex: "0 0 33%", fontSize: "20px" }}>
                                    {translatedWord}
                                </div>

                                <div
                                    className={`press-image-btn mx-auto ${isListening ? "recording" : ""}`}
                                    onMouseDown={handlePressStart}
                                    onMouseUp={handlePressEnd}
                                    onMouseLeave={handlePressEnd}
                                    onTouchStart={(e) => { e.preventDefault(); handlePressStart(e); }}
                                    onTouchEnd={(e) => { e.preventDefault(); handlePressEnd(e); }}
                                    onTouchCancel={(e) => { e.preventDefault(); handlePressEnd(e); }}
                                    style={{
                                        width: "clamp(90px, 10vw, 110px)", height: "clamp(90px, 10vw, 110px)", flex: "0 0 63%",
                                        backgroundImage: `url(${buttonImages[lanText] || enbutton})`,
                                        backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center",
                                        cursor: isRecordingComplete || !micPermissionGranted || isNavigating || disable ? "not-allowed" : "pointer",
                                        userSelect: "none",
                                        opacity: isRecordingComplete || !micPermissionGranted || disable ? 0.5 : 1,
                                        transition: "all 0.2s",
                                        WebkitTouchCallout: "none", WebkitUserSelect: "none",
                                        WebkitTapHighlightColor: "transparent", touchAction: "none",
                                        pointerEvents: isRecordingComplete || !micPermissionGranted || isNavigating || disable ? "none" : "auto"
                                    }}
                                />
                            </div>
                        </div>

                    </Col>
                </Row>
            </div>

            <RotatePhonePopup excludeWhen={[]} />

            <AnimatePresence>
                {activeLottie && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 1, delay: 1 }}
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ zIndex: 7, backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(5px)', overflow: 'hidden' }}
                    >
                        {[
                            { width: 35, style: { top: '50%', left: '15%' }, animate: { x: [0, 15, -10, 8, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, 10, -10, 5, -5, 0] }, duration: 5, delay: 0 },
                            { width: 20, style: { top: '55%', right: '10%' }, animate: { x: [0, -12, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, -15, 15, -8, 8, 0] }, duration: 6, delay: 0.5 },
                            { width: 6, style: { bottom: '65%', left: '12%' }, animate: { x: [0, 10, -8, 6, -4, 0], y: [0, -12, 9, -6, 4, 0], rotate: [0, 20, -20, 10, -10, 0] }, duration: 7, delay: 1 },
                            { width: 35, style: { top: '30%', right: '35%' }, animate: { x: [0, 15, -10, 8, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, 10, -10, 5, -5, 0] }, duration: 5, delay: 0 },
                            { width: 10, style: { top: '35%', right: '10%' }, animate: { x: [0, -12, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, -15, 15, -8, 8, 0] }, duration: 6, delay: 0.5 },
                            { width: 6, style: { top: '25%', right: '12%' }, animate: { x: [0, 10, -8, 6, -4, 0], y: [0, -12, 9, -6, 4, 0], rotate: [0, 20, -20, 10, -10, 0] }, duration: 7, delay: 1 },
                            { width: 10, style: { bottom: '35%', right: '15%' }, animate: { x: [0, -15, 10, -8, 5, 0], y: [0, -10, 8, -5, 3, 0], rotate: [0, -12, 12, -6, 6, 0] }, duration: 5.5, delay: 0.3 },
                            { width: 7, style: { top: '41%', left: '50%', transform: 'translateX(-50%)' }, animate: { x: [0, 8, -6, 5, -3, 0], y: [0, 12, -8, 6, -4, 0], rotate: [0, 15, -15, 8, -8, 0] }, duration: 6.5, delay: 1.5 },
                            { width: 5, style: { top: '45%', left: '8%' }, animate: { x: [0, 12, -9, 7, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, -18, 18, -9, 9, 0] }, duration: 7.5, delay: 0.8 },
                            { width: 9, style: { top: '50%', right: '8%' }, animate: { x: [0, -10, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, 12, -12, 6, -6, 0] }, duration: 5.8, delay: 1.2 },
                            { width: 12, style: { bottom: '30%', left: '50%', transform: 'translateX(-50%)' }, animate: { x: [0, -8, 6, -5, 3, 0], y: [0, -10, 7, -5, 3, 0], rotate: [0, 25, -25, 12, -12, 0] }, duration: 6.8, delay: 0.6 },
                        ].map((s, i) => (
                            <motion.img key={i} src={starImgSrc || star} alt="star" width={s.width}
                                className="position-absolute" style={s.style}
                                animate={s.animate}
                                transition={{ duration: s.duration, repeat: Infinity, repeatType: "loop", ease: "easeInOut", delay: s.delay }}
                            />
                        ))}

                        {isIOS && (
                            <Button
                                onClick={() => navigateToNextScreen(audioBlob, finalDecibel)}
                                className="rounded-pill border-0 mx-auto GTMedium text-black position-absolute"
                                disabled={!audioBlob}
                                style={{
                                    padding: "10px 1px", height: "50px", width: "min(350px, 80vw)", maxWidth: "350px",
                                    bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                                    background: getBackgroundColor(finalDecibel),
                                    pointerEvents: audioBlob ? 'auto' : 'none',
                                    cursor: 'pointer', touchAction: 'none', WebkitTapHighlightColor: 'transparent',
                                }}
                            >
                                <span className='GTMedium' style={{ fontSize: "clamp(21px, 4.5vw, 27px)", lineHeight: 1, whiteSpace: "nowrap" }}>
                                    {t?.common?.next || 'Next'}
                                </span>
                            </Button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {alertModal.show && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)' }}>
                    <div className="popup-bg rounded-4 p-3 m-3"
                        style={{ maxWidth: '420px', width: '90%', boxShadow: '0 20px 55px rgba(0, 0, 0, 0.3)', maxHeight: '90vh', overflowY: 'auto' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div className="text-center rounded-4 p-2 mx-auto" style={{ maxWidth: "360px" }}>
                            <div className="d-flex align-items-center justify-content-center mx-auto mb-2"
                                style={{ width: "50px", height: "50px", borderRadius: "50%", background: "#FFECEC", fontSize: "22px" }}>🎤</div>
                            <p className="mb-2 fw-semibold popup-main-color" style={{ fontSize: "18px" }}>{t.whoTalkin.micPermissionTitle}</p>
                            <p className="mb-3" style={{ fontSize: "13px", lineHeight: "1.3", color: "#A7A7A7" }}>{t.whoTalkin.micPermissionDesc}</p>
                            <div className="text-start popup-center-bg d-flex flex-column justify-content-around align-items-center popup-border-dashed"
                                style={{ fontSize: "16px", lineHeight: "1.7", borderRadius: "12px", padding: "12px 12px 13px 12px" }}>
                                <h6 className="GTBold text-center popup-main-color">{t.steps}</h6>
                                <ol className="m-0 my-4 popup-main-color">
                                    <li className="mb-2">{t.whoTalkin.step1.text} <b>{t.whoTalkin.step1.bold}</b> {t.whoTalkin.step1.text2}</li>
                                    <li className="mb-2">{t.whoTalkin.step2.text} <b>{t.whoTalkin.step2.bold}</b> {t.whoTalkin.step2.text2}</li>
                                    <li className="mb-2">{t.whoTalkin.step3.text} <b>{t.whoTalkin.step3.bold}</b> {t.whoTalkin.step3.text2}</li>
                                    <li>{t.whoTalkin.step4.text} <b>{t.whoTalkin.step4.bold}</b> {t.whoTalkin.step4.text2}</li>
                                </ol>
                            </div>
                        </div>
                        <div className="text-center mt-3">
                            <button className="btn fw-semibold"
                                style={{ width: "90%", backgroundColor: '#FFA400', color: 'white', borderRadius: '10px', fontSize: '16px', padding: '10.px' }}
                                onClick={() => { setAlertModal({ show: false }); window.location.reload(); }}>
                                {t.whoTalkin.close}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {alertModal2.show && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)' }}>
                    <div className="popup-bg rounded-4 p-3 m-3"
                        style={{ maxWidth: '420px', width: '90%', boxShadow: '0 20px 55px rgba(0, 0, 0, 0.3)', maxHeight: '90vh', overflowY: 'auto' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div className="text-center rounded-4 p-2 mx-auto" style={{ maxWidth: "360px" }}>
                            <div className="d-flex align-items-center justify-content-center mx-auto mb-2"
                                style={{ width: "50px", height: "50px", borderRadius: "50%", background: "#FFECEC", fontSize: "22px" }}>🎤</div>
                            <p className="mb-2 fw-semibold popup-main-color" style={{ fontSize: "18px" }}>{t.whoTalkin.micPermissionTitle}</p>
                            <p className="mb-3" style={{ fontSize: "13px", lineHeight: "1.3", color: "#A7A7A7" }}>{t.whoTalkin.micPermissionDesc}</p>
                            <div className="text-start popup-center-bg d-flex flex-column justify-content-around align-items-center popup-border-dashed"
                                style={{ fontSize: "16px", lineHeight: "1.7", borderRadius: "12px", padding: "12px 12px 13px 12px" }}>
                                <h6 className="GTBold text-center popup-main-color">{t.whoTalkin.steps}</h6>
                                <ol className="m-0 my-4 popup-main-color">
                                    <li className="mb-2 popup-main-color ios-item">
                                        <span className="pe-2">{t.whoTalkin.ios1.text}</span>
                                        (<b className="icon ios-icon-1"></b>)
                                        <b className="px-2">{t.whoTalkin.ios1.bold}</b>
                                        ( <b className="icon ios-icon-2 mx-1"></b> )
                                        <span className="ps-2">{t.whoTalkin.ios1.text2}</span>
                                    </li>
                                    <li className="mb-2 popup-main-color">{t.whoTalkin.ios2.text} <b>{t.whoTalkin.ios2.bold}</b> {t.whoTalkin.ios2.text2}</li>
                                    <li className="mb-2 popup-main-color">{t.whoTalkin.ios3.text} <b>{t.whoTalkin.ios3.bold}</b> {t.whoTalkin.ios3.text2}</li>
                                    <li className="popup-main-color">{t.whoTalkin.ios4.text} <b>{t.whoTalkin.ios4.bold}</b> {t.whoTalkin.ios4.text2}</li>
                                </ol>
                            </div>
                        </div>
                        <div className="text-center mt-3">
                            <button className="btn fw-semibold"
                                style={{ width: "90%", backgroundColor: '#FFA400', color: 'white', borderRadius: '10px', fontSize: '16px', padding: '10.px' }}
                                onClick={() => { setAlertModal2({ show: false }); window.location.reload(); }}>
                                {t.whoTalkin.close}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Category4;